import React from "react";

import { Text, View } from "@react-pdf/renderer";

const parseHTML = (html) => {
  if (!html) return [];

  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = html;

  const elements = [];

  const parseNode = (node, inheritedStyle = {}) => {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent.trim();
      if (text) {
        return { type: "text", content: text, style: inheritedStyle };
      }
      return null;
    }

    if (node.nodeType !== Node.ELEMENT_NODE) return null;

    const tagName = node.tagName.toLowerCase();
    const newStyle = { ...inheritedStyle };
    let blockType = null;

    switch (tagName) {
      case "strong":
      case "b":
        newStyle.fontWeight = 700;
        break;
      case "em":
      case "i":
        newStyle.fontStyle = "italic";
        break;
      case "u":
        newStyle.textDecoration = "underline";
        break;
      case "h1":
        blockType = "heading1";
        newStyle.fontSize = 18;
        newStyle.lineHeight = 1.4;
        newStyle.fontWeight = 500;
        newStyle.marginTop = 15;
        newStyle.marginBottom = 8;
        break;
      case "h2":
        blockType = "heading2";
        newStyle.fontSize = 16;
        newStyle.lineHeight = 1.4;
        newStyle.fontWeight = 500;
        newStyle.marginTop = 12;
        newStyle.marginBottom = 6;
        break;
      case "h3":
        blockType = "heading3";
        newStyle.fontSize = 14;
        newStyle.lineHeight = 1.4;
        newStyle.fontWeight = 500;
        newStyle.marginTop = 10;
        newStyle.marginBottom = 5;
        break;
      case "p":
        blockType = "paragraph";
        break;
      case "ul":
      case "ol":
        blockType = tagName === "ul" ? "unorderedList" : "orderedList";
        newStyle.marginBottom = 5;
        newStyle.marginLeft = 5;
        break;
      case "li":
        blockType = "listItem";
        newStyle.marginBottom = 0;
        break;
      case "br":
        return { type: "lineBreak" };
      case "hr":
        blockType = "horizontalRule";
        newStyle.borderBottomWidth = 1;
        newStyle.borderBottomColor = "#303030";
        newStyle.marginVertical = 8;
        break;
      default:
        break;
    }

    const children = [];
    node.childNodes.forEach((child) => {
      const parsed = parseNode(child, newStyle);
      if (parsed) {
        if (Array.isArray(parsed)) {
          children.push(...parsed);
        } else {
          children.push(parsed);
        }
      }
    });

    if (blockType) {
      return {
        type: "block",
        blockType,
        style: newStyle,
        children,
      };
    }

    return children;
  };

  tempDiv.childNodes.forEach((node) => {
    const parsed = parseNode(node);
    if (parsed) {
      if (Array.isArray(parsed)) {
        elements.push(...parsed);
      } else {
        elements.push(parsed);
      }
    }
  });

  return elements;
};

const renderParsedHTML = (elements, listCounter = { ol: 0 }) => {
  return elements.map((element, idx) => {
    if (element.type === "lineBreak") {
      return <Text key={idx}>{"\n"}</Text>;
    }

    if (element.type === "text") {
      return (
        <Text key={idx} style={element.style}>
          {element.content}
        </Text>
      );
    }

    if (element.type === "block") {
      const { blockType, style, children } = element;

      if (blockType === "listItem") {
        return (
          <View key={idx} style={{ flexDirection: "row", ...style }}>
            <Text style={{ marginRight: 5 }}>•</Text>
            <View style={{ flex: 1 }}>
              {renderParsedHTML(children, listCounter)}
            </View>
          </View>
        );
      }

      if (blockType === "orderedList") {
        listCounter.ol = 0;
        return (
          <View key={idx} style={style}>
            {children.map((child, childIdx) => {
              listCounter.ol++;
              return (
                <View key={childIdx} style={{ flexDirection: "row" }}>
                  <Text style={{ marginRight: 5, marginLeft: 5 }}>
                    {listCounter.ol}.
                  </Text>
                  <View style={{ flex: 1 }}>
                    {child.children &&
                      renderParsedHTML(child.children, listCounter)}
                  </View>
                </View>
              );
            })}
          </View>
        );
      }

      if (blockType === "unorderedList") {
        return (
          <View key={idx} style={style}>
            {renderParsedHTML(children, listCounter)}
          </View>
        );
      }

      // Для параграфов и заголовков оборачиваем дочерние элементы в один Text
      if (blockType === "paragraph" || blockType.startsWith("heading")) {
        return (
          <Text key={idx} style={style}>
            {children.map((child, childIdx) => (
              <React.Fragment key={childIdx}>
                {renderInlineElement(child, childIdx)}
                {childIdx < children.length - 1 && " "}
              </React.Fragment>
            ))}
          </Text>
        );
      }

      return (
        <View key={idx} style={style}>
          {renderParsedHTML(children, listCounter)}
        </View>
      );
    }

    return null;
  });
};

// Вспомогательная функция для рендеринга inline-элементов внутри Text
const renderInlineElement = (element, key) => {
  if (element.type === "lineBreak") {
    return "\n";
  }

  if (element.type === "text") {
    return (
      <Text key={key} style={element.style}>
        {element.content}
      </Text>
    );
  }

  if (element.type === "block" && element.children) {
    return element.children.map((child, idx) =>
      renderInlineElement(child, `${key}-${idx}`)
    );
  }

  return null;
};

export const HTMLRenderer = ({ html, style }) => {
  const elements = parseHTML(html);

  return <View style={style}>{renderParsedHTML(elements)}</View>;
};
